<template>
  <div class="revenue-chart-container">
    <div class="chart-controls">
      <h3>Revenue Over Time</h3>
      <div class="group-by-selector">
        <label>Group By:</label>
        <select v-model="groupBy" @change="fetchData">
          <option value="day">Day</option>
          <option value="week">Week</option>
          <option value="month">Month</option>
        </select>
      </div>

      <div v-if="warehouses && warehouses.length > 0" class="warehouse-selector">
        <label>Warehouse:</label>
        <select v-model="selectedWarehouse" @change="fetchData">
          <option value="">All Warehouses</option>
          <option v-for="warehouse in warehouses" :key="warehouse.id" :value="warehouse.id">
            {{ warehouse.name }}
          </option>
        </select>
      </div>
    </div>
    
    <div class="chart-wrapper">
      <div v-if="loading" class="loading-indicator">
        <i class="material-icons spin">refresh</i>
        Loading data...
      </div>
      <EChartsLineChart
        v-else-if="chartData.labels.length > 0" 
        ref="revenueLine"
        :labels="chartData.labels"
        :values="chartData.datasets[0].data"
        series-name="Revenue"
        aria-label="Revenue over time"
        :is-dark="isDark" 
      />
      <div v-else class="no-data">
        No data available for the selected period
      </div>
    </div>
    <div class="card-footer">
      <router-link :to="reportRoute" class="view-report-link">
        View full report
        <i class="material-icons">arrow_forward</i>
      </router-link>
    </div>
  </div>
</template>

<script>
import EChartsLineChart from './EChartsLineChart.vue';
import SalesFetchApi from '../../utils/fetch/sales-fetch-api';
import { formatBusinessChartLabel, getBusinessDateRangeForPreset } from '../../utils/business-date';
import { mapState } from 'vuex';

export default {
  name: 'RevenueOverTimeChart',
  components: {
    EChartsLineChart
  },
  props: {
    isDark: {
      type: Boolean,
      default: false
    },
    initialDateRange: {
      type: String,
      default: 'last30days'
    },
    customDateRange: {
      type: Object,
      default: () => ({
        start: '',
        end: ''
      })
    },
    warehouses: {
      type: Array,
      default: () => []
    },
    includePilotSales: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      selectedRange: this.initialDateRange,
      startDate: '',
      endDate: '',
      groupBy: 'day',
      selectedWarehouse: '',
      salesData: [],
      lastRequestKey: '',
      pendingRequestKey: '',
      fetchRequestId: 0,
      chartData: {
        labels: [],
        datasets: [
          {
            label: 'Revenue',
            backgroundColor: 'rgba(13, 45, 44, 0.1)',
            borderColor: 'rgba(13, 45, 44, 1)',
            pointBackgroundColor: 'rgba(13, 45, 44, 1)',
            data: [],
            fill: true,
            lineTension: 0.3,
            pointRadius: 4,
            pointHoverRadius: 6,
            pointHitRadius: 10
          }
        ]
      }
    };
  },
  computed: {
    ...mapState("misc", ["theme", "THEMES"]),
    reportRoute() {
      const query = {};
      if (this.startDate && this.endDate) {
        query.date_range_start = this.startDate;
        query.date_range_end = this.endDate;
      }
      if (this.includePilotSales) query.includePilotSales = "true";
      return {
        path: "/reports/sale-report",
        query
      };
    }
  },
  created() {
    this.updateDateRange();
  },
  watch: {
    // Watch for changes to the initialDateRange prop from the parent
    initialDateRange(newValue) {
      if (newValue !== this.selectedRange) {
        this.selectedRange = newValue;
        this.updateDateRange();
      }
    },
    // Watch for changes to the customDateRange from the parent
    customDateRange: {
      handler(newValue) {
        if (this.selectedRange === 'custom' && (newValue.start !== this.startDate || newValue.end !== this.endDate)) {
          this.startDate = newValue.start;
          this.endDate = newValue.end;
          this.fetchData();
        }
      },
      deep: true
    },
    isDark() {
      this.$nextTick(() => {
        if (this.$refs.revenueLine && this.$refs.revenueLine.refresh) {
          this.$refs.revenueLine.refresh();
        }
      });
    },
    includePilotSales() {
      this.fetchData();
    }
  },
  methods: {
    // Method to be called from the parent component to set date range
    setDateRange(range, customDates = null) {
      this.selectedRange = range;

      if (customDates && customDates.start && customDates.end) {
        this.startDate = customDates.start;
        this.endDate = customDates.end;
        this.fetchData();
        return;
      }

      this.updateDateRange();
    },
    updateDateRange() {
      if (this.selectedRange === 'custom') {
        if (!this.startDate || !this.endDate) {
          const fallbackRange = getBusinessDateRangeForPreset('last30days');
          this.startDate = fallbackRange.start;
          this.endDate = fallbackRange.end;
        }
      } else {
        const range = getBusinessDateRangeForPreset(this.selectedRange);
        this.startDate = range.start;
        this.endDate = range.end;
      }
      
      this.fetchData();
    },
    async fetchData() {
      const requestKey = [this.startDate, this.endDate, this.groupBy, this.selectedWarehouse, this.includePilotSales].join('|');
      if (requestKey === this.pendingRequestKey) return;
      const requestId = ++this.fetchRequestId;
      this.pendingRequestKey = requestKey;

      try {
        this.loading = true;
        
        // Always use explicit date range parameters
        const options = {
          groupBy: this.groupBy,
          startDate: this.startDate,
          endDate: this.endDate,
          includePilotSales: this.includePilotSales
        };
        
        // Add warehouse filtering if selected
        if (this.selectedWarehouse) {
          options.warehouse_ids = this.selectedWarehouse;
        }
        
        const response = await SalesFetchApi.getSalesOverTime(options);
        if (requestId !== this.fetchRequestId) return;
        
        if (response && response.result) {
          this.salesData = response.result;
          this.processChartData();
          this.lastRequestKey = requestKey;
        } else {
          // Reset chart data if no results
          this.chartData = {
            labels: [],
            datasets: [{
              label: 'Revenue',
              backgroundColor: 'rgba(13, 45, 44, 0.1)',
              borderColor: 'rgba(13, 45, 44, 1)',
              pointBackgroundColor: 'rgba(13, 45, 44, 1)',
              data: [],
              fill: true,
              lineTension: 0.3,
              pointRadius: 4,
              pointHoverRadius: 6,
              pointHitRadius: 10
            }]
          };
        }
      } catch (error) {
        if (requestId !== this.fetchRequestId) return;
        console.error('Error fetching sales data:', error);
        // Reset chart data if error
        this.chartData = {
          labels: [],
          datasets: [{
            label: 'Revenue',
            backgroundColor: 'rgba(13, 45, 44, 0.1)',
            borderColor: 'rgba(13, 45, 44, 1)',
            pointBackgroundColor: 'rgba(13, 45, 44, 1)',
            data: [],
            fill: true,
            lineTension: 0.3,
            pointRadius: 4,
            pointHoverRadius: 6,
            pointHitRadius: 10
          }]
        };
      } finally {
        if (requestId === this.fetchRequestId) {
          this.pendingRequestKey = '';
          this.loading = false;
        }
      }
    },
    processChartData() {
      if (!this.salesData || !this.salesData.length) {
        // Reset chart data if no results
        this.chartData = {
          labels: [],
          datasets: [{
            label: 'Revenue',
            backgroundColor: 'rgba(90, 120, 255, 0.1)',
            borderColor: '#5a78ff',
            pointBackgroundColor: '#5a78ff',
            data: [],
            fill: true,
            lineTension: 0.3,
            pointRadius: 4,
            pointHoverRadius: 6,
            pointHitRadius: 10
          }]
        };
        return;
      }

      const sortedData = [...this.salesData].sort((a, b) =>
        String(a.businessDate || a.bucketStartIso || a.timestamp).localeCompare(String(b.businessDate || b.bucketStartIso || b.timestamp))
      );
      
      const labels = sortedData.map(item => formatBusinessChartLabel(item, this.groupBy));
      
      // Extract revenue values
      const data = sortedData.map(item => 
        Math.round(item.revenue * 100) / 100
      );
      
      this.chartData = {
        labels,
        datasets: [{
          label: 'Revenue',
          backgroundColor: 'rgba(90, 120, 255, 0.1)',
          borderColor: '#5a78ff',
          pointBackgroundColor: '#5a78ff',
          data,
          fill: true,
          lineTension: 0.3,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointHitRadius: 10
        }]
      };

      this.$nextTick(() => {
        if (this.$refs.revenueLine && this.$refs.revenueLine.refresh) {
          this.$refs.revenueLine.refresh();
        }
      })
    },
    exportDataCSV() {
      // Convert chart data to CSV format
      const headers = ['Date', 'Revenue'];
      let csvContent = headers.join(',') + '\n';
      
      for (let i = 0; i < this.chartData.labels.length; i++) {
        const row = [
          this.chartData.labels[i],
          this.chartData.datasets[0].data[i]
        ];
        csvContent += row.join(',') + '\n';
      }
      
      // Create download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      
      link.setAttribute('href', url);
      link.setAttribute('download', `revenue_${this.startDate}_to_${this.endDate}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/variables.scss";

.revenue-chart-container {
  background-color: $lightCardBg;
  border-radius: 8px;
  box-shadow: $cardShadow;
  padding: $cardPaddingY $cardPaddingX;
  margin-bottom: $sectionGap;
  border: 1px solid $lightBorderColor;
  transition: all 0.2s ease-in-out;
  width: 100%;
  
  &:hover {
    box-shadow: $cardShadowHover;
  }
}

.dark .revenue-chart-container {
  background-color: $darkCardBg;
  border-color: $darkBorderColor;
  color: $darkTextColor;
}

.chart-controls {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  gap: 8px;
}

.chart-controls h3 {
  margin: 0;
  font-size: 16px;
  color: $primaryColor;
  font-weight: 600;
}

.dark .chart-controls h3 {
  color: $secondaryColorDark;
}

.date-range-selector, 
.group-by-selector,
.warehouse-selector {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 6px 0;
}

select, input {
  padding: 6px 8px;
  border: 1px solid $lightBorderColor;
  border-radius: 4px;
  font-size: 13px;
  background-color: white;
}

.dark select, .dark input {
  background-color: $darkInputBg;
  border-color: $darkBorderColor;
  color: $darkTextColor;
}

.custom-date-range {
  display: flex;
  gap: 8px;
  margin-top: 8px;
  flex-wrap: wrap;
}

.custom-date-range button {
  padding: 4px 12px;
  background-color: $primaryColor;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
}

.dark .custom-date-range button {
  background-color: $primaryColorDark;
}

.chart-wrapper { position: relative; height: $chartHeightLarge; margin-bottom: 0; }

.loading-indicator, 
.no-data {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  color: $textColor;
  opacity: 0.7;
  font-size: 14px;
}

.dark .loading-indicator,
.dark .no-data {
  color: $darkTextColor;
  opacity: 0.7;
}

.loading-indicator .material-icons {
  margin-right: 8px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@media (max-width: 768px) {
  .chart-controls {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .date-range-selector, 
  .group-by-selector,
  .warehouse-selector {
    width: 100%;
    margin: 6px 0;
  }
  
  .custom-date-range {
    flex-direction: column;
    width: 100%;
  }
  
  select, input {
    width: 100%;
  }
}

.card-footer { display: flex; justify-content: flex-end; margin-top: 8px; }
.view-report-link {
  display: inline-flex; align-items: center; gap: 6px; color: $primaryColor; text-decoration: none; font-weight: 600; font-size: 12px; padding: 6px 8px; border-radius: 6px; transition: background-color 0.2s ease;
  &:hover { background-color: rgba(13, 45, 44, 0.06); }
}
</style> 
